import { useI18n } from '../../contexts/I18nContext';
import type { VoiceCallDetail } from '../../lib/voiceCallsApi';
import {
  useAcceptCallTaskSuggestion,
  useCallTaskSuggestions,
  useDismissCallTaskSuggestion,
  useGenerateCallTaskSuggestions,
  useRestoreCallTaskSuggestion,
} from '../../hooks/useVoiceCalls';
import { TaskSuggestionsPanel } from '../meetings/TaskSuggestionsPanel';

/**
 * "Sugestii de sarcini" on a voice-room call — the same panel the meetings
 * page shows, wired to /api/voice-calls/:id/task-suggestions.
 *
 * The AI reads the call a couple of minutes after the last person leaves (the
 * sweep in the backend), so a page opened right after a call polls until the
 * proposals land instead of asking the reader to refresh. Nothing is proposed
 * for a call that is still going on: the discussion is not over yet.
 */
export interface VoiceCallTaskSuggestionsProps {
  call: VoiceCallDetail;
  /** Hide the decide/regenerate controls (cannot create tasks). */
  readOnly?: boolean;
}

/** How long after a call ends its suggestions are still expected to appear. */
const FRESH_CALL_MS = 15 * 60_000;

export function VoiceCallTaskSuggestions({
  call,
  readOnly = false,
}: VoiceCallTaskSuggestionsProps) {
  const { t } = useI18n();
  const live = call.endedAt === null;
  const endedRecently =
    call.endedAt !== null && Date.now() - new Date(call.endedAt).getTime() < FRESH_CALL_MS;

  const { data: suggestions = [], isLoading } = useCallTaskSuggestions(call.id, endedRecently);
  const generateMutation = useGenerateCallTaskSuggestions(call.id);
  const acceptMutation = useAcceptCallTaskSuggestion(call.id);
  const dismissMutation = useDismissCallTaskSuggestion(call.id);
  const restoreMutation = useRestoreCallTaskSuggestion(call.id);

  return (
    <TaskSuggestionsPanel
      suggestions={suggestions}
      isLoading={isLoading}
      // While the call is on, the transcript is still growing: the panel says
      // it is waiting rather than offering to read half a discussion.
      processing={live}
      hasTranscript={!live && call.segmentCount > 0}
      readOnly={readOnly}
      text={{
        hint: t('teamChat.suggestions.hint'),
        empty: t('teamChat.suggestions.empty'),
        needTranscript: live
          ? t('teamChat.suggestions.live')
          : t('teamChat.suggestions.needTranscript'),
      }}
      actions={{
        generate: () => generateMutation.mutateAsync(),
        generating: generateMutation.isPending,
        accept: (id, input) => acceptMutation.mutateAsync({ id, input }),
        dismiss: (id) => dismissMutation.mutateAsync(id),
        restore: (id) => restoreMutation.mutateAsync(id),
      }}
    />
  );
}
